/**
 * Turning on reminders.
 *
 * Two constraints shape this, both platform facts rather than choices:
 *
 *   - The permission prompt must come from a user gesture. Asking on page load is both
 *     rejected by browsers and, for someone impaired, a modal appearing unbidden.
 *   - On iOS push works ONLY in a PWA added to the Home Screen. In a Safari tab the
 *     API is simply absent, so the honest thing is to say so rather than show a button
 *     that cannot work.
 */
import type { AppConfig } from './config.js';
import { apiFetch } from './api/client.js';

export type PushState =
  | 'unsupported'
  | 'needs-install'
  | 'prompt'
  | 'granted'
  | 'denied';

/** True when the page is running as an installed app rather than a browser tab. */
export function isInstalled(): boolean {
  const standalone = (navigator as unknown as { standalone?: boolean }).standalone;
  return window.matchMedia('(display-mode: standalone)').matches || standalone === true;
}

function isIos(): boolean {
  return /iP(hone|ad|od)/.test(navigator.userAgent);
}

export function pushState(): PushState {
  const supported =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  // On iOS the APIs only appear once the app is installed, so an uninstalled iPhone
  // reports "unsupported" when the real answer is "add it to your Home Screen first".
  if (!supported) return isIos() && !isInstalled() ? 'needs-install' : 'unsupported';
  if (isIos() && !isInstalled()) return 'needs-install';

  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return 'prompt';
}

/**
 * Ask for permission and register. MUST be called from a click handler.
 *
 * Returns false rather than throwing: failing to enable reminders is a disappointment,
 * not an error worth interrupting anyone over.
 */
export async function enablePush(config: AppConfig, vapidPublicKey: string): Promise<boolean> {
  try {
    if (pushState() === 'granted') return true;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;

    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        // Required by every browser: a push that cannot be shown is not allowed.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      }));

    const json = subscription.toJSON() as {
      endpoint?: string;
      keys?: { p256dh?: string; auth?: string };
    };
    if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return false;

    await apiFetch(config, '/v1/push/subscriptions', {
      method: 'POST',
      body: {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        label: deviceLabel(),
      },
    });
    return true;
  } catch {
    return false;
  }
}

function deviceLabel(): string {
  if (isIos()) return isInstalled() ? 'iPhone (installed)' : 'iPhone';
  if (navigator.userAgent.includes('Android')) return 'Android';
  return 'Computer';
}

/** VAPID keys are base64url; PushManager wants raw bytes. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
